import type { AnalyticsExperimentAgentSummary } from '../types/analytics.types';
import type { ChatbotMessage, ChatbotMetric } from '../types/chatbot-message.types';
import { ArmId } from '../types/experiment.types';
import type {
  ArmComparisonResponse,
  CategoryPerformanceResponse,
  Experiment,
  ExperimentKpis,
  HealthInStockTrends,
} from '../types/experiment.types';
import { armIdLabelText, hasSecondTestArm } from '../utils/arm-labels';

/**
 * Details-mode narration. Mirrors the active Details sub-tab + Performance
 * variant on the right side of the page:
 *
 *   view='performance', performanceMode='default'  → buildPerformance
 *   view='performance', performanceMode='category' → buildPerformanceCategory
 *   view='health'                                  → buildHealth
 *
 * Every message in this file is data-derived. When a source query is
 * missing data (no backend endpoint yet, fetch errored, DRAFT experiment),
 * the matching section is **skipped** — we never splice in placeholder
 * winners, fake metric tables, or scripted delivery-issue copy. The
 * mock-laden behaviour that used to live here is preserved via MSW
 * fixtures so dev/demo mode (`VITE_MSW=true`) still renders a full panel.
 */

export type DetailsView = 'performance' | 'health';

/** Variant of the Performance sub-tab; resolved upstream by `usePerformanceViewMode`. */
export type PerformanceMode = 'default' | 'category';

interface DetailsInput {
  view: DetailsView;
  /** Active Performance variant. Ignored when `view === 'health'`. */
  performanceMode?: PerformanceMode | undefined;
  experiment?: Experiment | undefined;
  kpis?: ExperimentKpis | undefined;
  armComparison?: ArmComparisonResponse | undefined;
  inStockTrends?: HealthInStockTrends | undefined;
  /** Single payload that powers the entire category-mode chatbot section. */
  categoryPerformance?: CategoryPerformanceResponse | undefined;
  /**
   * Analytics-service-composed headline + notes. When supplied the
   * Performance intro (hardcoded narrative) is replaced with `headline`
   * followed by one narrative per note.
   */
  agentSummary?: AnalyticsExperimentAgentSummary | undefined;
}

/**
 * Arm count used for labelling when the experiment record isn't available.
 * `armsPresent` carries `test_b` only when the payload actually has a second
 * test arm, so it's a sound fallback for choosing between the 2-arm and 3-arm
 * naming rule (see `utils/arm-labels.ts`).
 */
function resolveArmCount(
  experiment: Experiment | undefined,
  armComparison: ArmComparisonResponse,
): number {
  if (experiment) return experiment.armCount;
  return armComparison.armsPresent.includes(ArmId.TEST_B) ? 3 : 2;
}

/** Builds the message list for the active Details sub-tab + Performance variant. */
export function buildDetailsNarration(input: DetailsInput): ChatbotMessage[] {
  if (input.view === 'health') return buildHealth(input);
  return input.performanceMode === 'category'
    ? buildPerformanceCategory(input)
    : buildPerformance(input);
}

// ─────────────────────────────────────────────────────────────────────────────
// Performance summary
// ─────────────────────────────────────────────────────────────────────────────

function buildPerformance({
  experiment,
  kpis,
  armComparison,
  agentSummary,
}: DetailsInput): ChatbotMessage[] {
  const name = experiment?.name ?? 'this experiment';
  const out: ChatbotMessage[] = [];

  // Intro: prefer the analytics-service-composed headline + notes; fall back
  // to a minimal data-driven greeting that only emits subtitle pieces we can
  // actually source from `experiment` + `kpis`.
  if (agentSummary) {
    out.push({ kind: 'narrative', id: 'p-intro', text: agentSummary.headline });
    agentSummary.notes.forEach((note, idx) => {
      out.push({ kind: 'narrative', id: `p-note-${String(idx)}`, text: note });
    });
  } else {
    out.push({
      kind: 'narrative',
      id: 'p-intro',
      text: `Here's what's happening with ${name}`,
    });
    const subtitle = formatSubtitle(experiment, kpis);
    if (subtitle) {
      out.push({ kind: 'narrative', id: 'p-subtitle', text: subtitle });
    }
  }

  // Arm-comparison-derived winner + per-row KPI bullets. Skipped entirely
  // when the source query has no data, or when no winner could be determined
  // (zero-traffic run), so the chatbot never invents a winner (AGENT-196).
  if (armComparison && armComparison.rows.length > 0 && armComparison.winner) {
    const winner = armComparison.winner;
    const armCount = resolveArmCount(experiment, armComparison);
    const winnerLabel = armIdLabelText(winner);
    out.push({ kind: 'narrative', id: 'p-winner', text: `Winner so far: ${winnerLabel}` });

    armComparison.rows.forEach((row, idx) => {
      const primary = pickWinnerValue(row, winner);
      const lift = pickWinnerLift(row, winner);
      out.push({
        kind: 'kpi-bullet',
        id: `p-kpi-${String(idx)}-${row.metric}`,
        label: row.metric,
        primary,
        ...(lift ? { lift } : {}),
      });
    });

    const runnerUp = deriveRunnerUpNarrative(armComparison, armCount);
    if (runnerUp) {
      out.push({ kind: 'narrative', id: 'p-runner-up-note', text: runnerUp });
    }
  }

  out.push({ kind: 'inline-icon-actions', id: 'p-icons', count: 9 });
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Performance summary — category mode
// ─────────────────────────────────────────────────────────────────────────────

function buildPerformanceCategory({
  experiment,
  categoryPerformance,
}: DetailsInput): ChatbotMessage[] {
  const name = experiment?.name ?? 'this experiment';
  const out: ChatbotMessage[] = [
    { kind: 'narrative', id: 'pc-intro', text: `Category Performance - ${name}` },
  ];

  if (categoryPerformance) {
    // `overallDidLift` is optional — the analytics endpoint serves no overall
    // aggregate yet, so skip the card rather than render an empty/NaN one.
    const overall = categoryPerformance.overallDidLift;
    const overallMetrics: ChatbotMetric[] = [];
    if (overall) {
      pushSignedMetric(overallMetrics, 'Revenue', overall.revenuePct);
      pushSignedMetric(overallMetrics, 'Conversion Value', overall.conversionValuePct);
      pushSignedMetric(overallMetrics, 'Conversions', overall.conversionsPct);
      pushSignedMetric(overallMetrics, 'Impressions', overall.impressionsPct);
    }
    if (overallMetrics.length > 0) {
      out.push({
        kind: 'labeled-card',
        id: 'pc-overall-did',
        title: 'Overall DID lift',
        metrics: overallMetrics,
      });
    }

    if (categoryPerformance.categoryStandouts.length > 0) {
      const standoutMetrics: ChatbotMetric[] = categoryPerformance.categoryStandouts.map((s) => ({
        label: s.label,
        value: s.primary,
        ...(s.subValue ? { subValue: s.subValue } : {}),
        ...(s.subValueTrend ? { subValueTrend: s.subValueTrend } : {}),
      }));
      out.push({
        kind: 'labeled-card',
        id: 'pc-standouts',
        title: 'Category Standouts',
        metrics: standoutMetrics,
      });
    }
  }

  out.push({ kind: 'inline-icon-actions', id: 'pc-icons', count: 9 });
  return out;
}

function pushSignedMetric(
  bucket: ChatbotMetric[],
  label: string,
  value: number | null | undefined,
): void {
  if (value === null || value === undefined) return;
  bucket.push({ label, value: formatSignedPercent(value), trend: inferTrend(value) });
}

function formatSignedPercent(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${String(value)}%`;
}

function inferTrend(value: number): 'up' | 'down' | 'neutral' {
  if (value > 0) return 'up';
  if (value < 0) return 'down';
  return 'neutral';
}

/**
 * Returns the runner-up arm narrative when the data supports it: the runner-up's
 * lift is positive on every row AND smaller than the winner's lift on every row.
 * Returns `null` when the pattern doesn't hold so the chatbot stays honest.
 *
 * A 2-arm experiment has no second test arm to be a runner-up, so it bails on
 * `armCount` first. `armsPresent` alone isn't enough — it's derived from the
 * analytics payload's nulls and can disagree with the workflow-service arm
 * count, which is the ground truth the tables render against.
 */
function deriveRunnerUpNarrative(
  armComparison: ArmComparisonResponse,
  armCount: number,
): string | null {
  if (armComparison.rows.length === 0) return null;
  if (!hasSecondTestArm(armCount)) return null;

  const winner = armComparison.winner;
  if (winner === null) return null;
  // Runner-up only applies when winner is a test arm and the other test arm exists.
  const runnerUp =
    winner === ArmId.TEST_B && armComparison.armsPresent.includes(ArmId.TEST_A)
      ? ArmId.TEST_A
      : winner === ArmId.TEST_A && armComparison.armsPresent.includes(ArmId.TEST_B)
        ? ArmId.TEST_B
        : null;
  if (!runnerUp) return null;

  const consistentlyTrailing = armComparison.rows.every((row) => {
    const runnerUpLift = parseLiftValue(runnerUp === ArmId.TEST_A ? row.testALift : row.testBLift);
    const winnerLift = parseLiftValue(winner === ArmId.TEST_A ? row.testALift : row.testBLift);
    return runnerUpLift > 0 && runnerUpLift < winnerLift;
  });
  if (!consistentlyTrailing) return null;

  const runnerUpLabel = armIdLabelText(runnerUp);
  const winnerLabel = armIdLabelText(winner);
  return `${runnerUpLabel} is also outperforming control but trailing ${winnerLabel} across all key metrics.`;
}

/** Parse a lift string like `"+0.30%"`, `"-$3.50"`, `"+0.7x"`, `"-26.0%"` into a signed number. */
function parseLiftValue(s: string): number {
  const cleaned = s.replace(/[$,]/g, '');
  const match = /[-+]?\d+(\.\d+)?/.exec(cleaned);
  return match ? Number(match[0]) : 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Health summary
// ─────────────────────────────────────────────────────────────────────────────

function buildHealth({ experiment, inStockTrends }: DetailsInput): ChatbotMessage[] {
  const name = experiment?.name ?? 'this experiment';
  const out: ChatbotMessage[] = [
    { kind: 'narrative', id: 'h-intro', text: `Health Summary - ${name}` },
  ];

  // Real `/health` does not carry the per-arm in-stock KPI shape this card
  // expects, so when the source is missing we render the intro only. The
  // hardcoded "Healthy / Balanced / Revenue after stock adjustment / no
  // categories were excluded" copy that used to live here is gone — those
  // were placeholder strings, not data.
  if (inStockTrends && inStockTrends.kpis.length > 0) {
    out.push({
      kind: 'status',
      id: 'h-in-stock',
      tone: 'success',
      badge: 'Healthy',
      title: 'In-Stock',
      metrics: inStockTrends.kpis.map((k) => ({
        label: k.label,
        ...(k.hint ? { hint: k.hint } : {}),
        value: k.value,
      })),
    });
  }

  out.push({ kind: 'inline-icon-actions', id: 'h-icons', count: 9 });
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function pickWinnerValue(row: ArmComparisonResponse['rows'][number], winner: ArmId): string {
  if (winner === ArmId.TEST_A) return row.testA;
  if (winner === ArmId.TEST_B) return row.testB;
  return row.control;
}

function pickWinnerLift(
  row: ArmComparisonResponse['rows'][number],
  winner: ArmId,
): string | undefined {
  if (winner === ArmId.TEST_A) return row.testALift;
  if (winner === ArmId.TEST_B) return row.testBLift;
  return undefined;
}

function formatPlatform(platform: Experiment['platform'] | undefined): string | null {
  if (platform === 'google') return 'Google Shopping';
  if (platform === 'meta') return 'Meta';
  return null;
}

/**
 * Assemble the Performance subtitle from real fields only — skips pieces
 * the experiment row hasn't populated yet (e.g. SKU count before the
 * Step-3 PATCH lands) instead of substituting placeholders.
 */
function formatSubtitle(
  experiment: Experiment | undefined,
  kpis: ExperimentKpis | undefined,
): string | null {
  const days = kpis?.daysElapsed ?? experiment?.progressDays;
  const arms = experiment?.armCount;
  const platform = formatPlatform(experiment?.platform);
  const sku = experiment?.skuCount?.toLocaleString();
  // `armCount` defaults to 0 in the mapper when the DTO has no
  // numberOfArms — treat that as "unknown" so we don't render "0-arm test".
  const hasArms = arms != null && arms > 0;

  const parts: string[] = [];
  if (days != null) parts.push(`${String(days)} days in`);
  if (hasArms) {
    parts.push(platform ? `${String(arms)}-arm test on ${platform}` : `${String(arms)}-arm test`);
  } else if (platform) {
    parts.push(`on ${platform}`);
  }
  if (parts.length === 0) return null;
  const base = parts.join(', ');
  return sku ? `${base} (${sku} SKUs)` : base;
}
